import { Controller } from "@hotwired/stimulus"
import { init } from "echarts"

export default class extends Controller {
  static values = {
    data: { type: Array, default: [] },
    homeColor: { type: String, default: "#059669" },
    awayColor: { type: String, default: "#2563eb" },
  }

  connect() {
    if (this.dataValue.length < 2) return

    this._resizeObserver = new ResizeObserver(() => this._chart?.resize())
    this._resizeObserver.observe(this.element)

    const points = this.dataValue.map(p => ({
      minute: p.m ?? 0,
      value: p.v ?? 0,
    }))

    const maxAbs = Math.max(...points.map(p => Math.abs(p.value)), 1)
    const minutes = points.map(p => p.minute)
    const halftimeIdx = points.findIndex(p => p.minute >= 45)

    const chart = init(this.element, null, { renderer: "svg" })
    chart.setOption({
      grid: { left: 4, right: 4, top: 4, bottom: 20 },
      xAxis: {
        type: "category",
        data: minutes.map(m => formatMinute(m)),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontSize: 9,
          color: "#9ca3af",
          interval: (idx) => {
            const m = minutes[idx]
            return m % 5 === 0 || m === 1
          },
          formatter: (label, idx) => {
            const m = minutes[idx]
            if (m === 45) return "HT"
            if (m >= 90) return `${Math.floor(m)}'`
            return `${Math.floor(m)}'`
          },
          hideOverlap: true,
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: "value",
        show: false,
        min: -maxAbs * 1.15,
        max: maxAbs * 1.15,
        splitLine: { show: false },
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(17,24,39,0.9)",
        borderWidth: 0,
        textStyle: { color: "#f3f4f6", fontSize: 12 },
        formatter: (params) => {
          const p = params[0]
          const pt = points[p.dataIndex]
          const color = pt.value >= 0
            ? this.homeColorValue
            : this.awayColorValue
          return `<div style="display:flex;align-items:center;gap:6px">
            <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${color}"></span>
            <span><strong>${formatMinute(pt.minute)}</strong> — momentum: ${pt.value > 0 ? "+" : ""}${pt.value}</span>
          </div>`
        },
      },
      series: [
        {
          type: "bar",
          data: points.map((p, i) => ({
            value: p.value,
            itemStyle: {
              color: p.value >= 0 ? this.homeColorValue : this.awayColorValue,
              opacity: Math.min(0.3 + Math.abs(p.value) / maxAbs * 0.7, 1),
              borderRadius: p.value >= 0 ? [2, 2, 0, 0] : [0, 0, 2, 2],
            },
          })),
          barWidth: "100%",
          barMaxWidth: 10,
          barGap: 0,
          showBackground: false,
          animation: false,
          markLine: halftimeIdx >= 0 ? {
            silent: true,
            symbol: "none",
            lineStyle: { color: "#6b7280", type: "dashed", width: 1 },
            label: { show: false },
            data: [{ xAxis: halftimeIdx }],
          } : undefined,
          label: {
            show: true,
            position: (p) => p.value >= 0 ? "top" : "bottom",
            fontSize: 9,
            color: "#9ca3af",
            formatter: (p) => {
              const v = Math.abs(p.value)
              return v >= maxAbs * 0.6 ? (p.value > 0 ? "+" : "") + p.value : ""
            },
            offset: [0, 3],
          },
        },
      ],
    })

    this._chart = chart
  }

  disconnect() {
    this._resizeObserver?.disconnect()
    this._chart?.dispose()
  }
}

function formatMinute(m) {
  if (m === Math.floor(m)) return `${m}'`
  const whole = Math.floor(m)
  const dec = Math.round((m - whole) * 10)
  return `${whole}+${dec}'`
}
